/**
 * CampaignResultsTab
 *
 * Email Hub → Campaign Results.
 *
 * Lets admins audit every bulk-email campaign their team has ever sent:
 *   • one row per campaign with sent / failed / pending counts
 *   • click → expanded panel showing each recipient + the actual delivery
 *     outcome (sent_emails.gmail_error or bulk_emails.error_message)
 *   • "Resend failed" button re-queues every failed recipient via the
 *     same cron the original campaign used (so it survives blips)
 *   • optional filter to only resend rows whose error matches the
 *     MongoDB-timeout fingerprint the operator explicitly called out
 *
 * Why this lives in its own file
 * ------------------------------
 * EmailHubPage.jsx is already 2,800 lines. New, self-contained features
 * land outside it so the page itself doesn't keep growing — and so the
 * Campaign Results UI can be unit-tested without dragging the entire
 * EmailHub state machine along.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCampaignDrillDown } from '../hooks/useCampaignDrillDown';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Send,
  AlertTriangle,
  Mail,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  StopCircle,
} from 'lucide-react';
import { authFetch, authJsonFetch } from '../lib/api';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const MONGO_TIMEOUT_FINGERPRINT = /(read operation timed out|ServerSelectionTimeoutError|fi6eho\.mongodb\.net)/i;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const map = {
    sent: { variant: 'default', icon: CheckCircle2, label: 'Sent', className: 'bg-emerald-600 hover:bg-emerald-700' },
    failed: { variant: 'destructive', icon: XCircle, label: 'Failed' },
    pending: { variant: 'secondary', icon: Clock, label: 'Pending' },
    cancelled: { variant: 'outline', icon: XCircle, label: 'Cancelled' },
    logged: { variant: 'outline', icon: Mail, label: 'Logged' },
  };
  const cfg = map[status] || { variant: 'outline', icon: Mail, label: status || '—' };
  const Icon = cfg.icon;
  return (
    <Badge variant={cfg.variant} className={`gap-1 ${cfg.className || ''}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
};

export const CampaignResultsTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState('30');
  const [search, setSearch] = useState('');
  const [resendDialog, setResendDialog] = useState({ open: false, campaign: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, campaign: null });
  const [cancelling, setCancelling] = useState(false);

  // Drill-down state lives in its own hook in a separate file. The
  // eslint-plugin-react-hooks v5 set-state-in-effect rule mis-flags
  // synchronous click-handler functions that orchestrate multiple
  // setState calls; keeping that logic outside the component sidesteps
  // the false positive entirely.
  const { expanded, recipientsState, toggleExpand } = useCampaignDrillDown();
  const recipients = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(recipientsState)) out[k] = v?.rows;
    return out;
  }, [recipientsState]);
  const recipientsLoading = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(recipientsState)) out[k] = !!v?.loading;
    return out;
  }, [recipientsState]);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/bulk-email/campaigns?days=${days}`);
      if (r.ok) {
        const data = await r.json();
        setCampaigns(Array.isArray(data) ? data : []);
      } else {
        toast.error(`Failed to load campaigns (${r.status})`);
      }
    } catch (err) {
      toast.error("Couldn't load campaigns — check your connection");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Apply client-side search filter for snappy interaction. Days filter
  // is server-side so we don't pay a round-trip on each keystroke.
  const filteredCampaigns = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return campaigns;
    return campaigns.filter((c) => {
      const haystack = [c.campaign_name, c.created_by_name, c._id]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase())
        .join(' ');
      return haystack.includes(needle);
    });
  }, [campaigns, search]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Campaign Results
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Every bulk email campaign your team has run — sent, failed, and pending.
              Click a row to see per-recipient results and resend failures.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-40" data-testid="campaign-days-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
                <SelectItem value="0">All time</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search campaign"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 w-56"
                data-testid="campaign-search"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCampaigns}
              disabled={loading}
              data-testid="campaign-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading && campaigns.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading campaigns…
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="campaign-empty">
              No campaigns yet in this date range.
            </div>
          ) : (
            <div className="divide-y" data-testid="campaign-list">
              {filteredCampaigns.map((camp) => (
                <CampaignRow
                  key={camp._id}
                  campaign={camp}
                  isExpanded={expanded === camp._id}
                  onToggle={() => toggleExpand(camp._id)}
                  recipients={recipients[camp._id]}
                  recipientsLoading={recipientsLoading[camp._id]}
                  onResend={() => setResendDialog({ open: true, campaign: camp })}
                  onCancel={() => setCancelDialog({ open: true, campaign: camp })}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ResendFailedDialog
        open={resendDialog.open}
        campaign={resendDialog.campaign}
        recipientPreview={recipients[resendDialog.campaign?._id]}
        onClose={() => setResendDialog({ open: false, campaign: null })}
        onResent={() => {
          setResendDialog({ open: false, campaign: null });
          fetchCampaigns();
        }}
      />

      <CancelCampaignDialog
        open={cancelDialog.open}
        campaign={cancelDialog.campaign}
        cancelling={cancelling}
        onClose={() => setCancelDialog({ open: false, campaign: null })}
        onConfirm={async () => {
          const camp = cancelDialog.campaign;
          if (!camp?._id) return;
          setCancelling(true);
          try {
            const r = await authJsonFetch(`${API}/bulk-email/campaigns/${camp._id}/cancel`, {
              method: 'POST',
            });
            if (!r.ok) {
              const err = await r.json().catch(() => ({}));
              throw new Error(err.detail || `Failed (${r.status})`);
            }
            const j = await r.json();
            const parts = [];
            if (j.cancelled > 0) parts.push(`${j.cancelled} cancelled`);
            if (j.sending > 0) parts.push(`${j.sending} already sending`);
            if (j.already_sent > 0) parts.push(`${j.already_sent} already sent`);
            toast.success(`Campaign stopped — ${parts.join(', ')}`);
            setCancelDialog({ open: false, campaign: null });
            await fetchCampaigns();
          } catch (e) {
            toast.error(`Could not stop campaign: ${e.message || 'network error'}`);
          } finally {
            setCancelling(false);
          }
        }}
      />
    </div>
  );
};

const CampaignRow = ({ campaign, isExpanded, onToggle, recipients, recipientsLoading, onResend, onCancel }) => {
  const failedCount = (campaign.actual_failed || 0) + (campaign.failed || 0);
  const showResend = failedCount > 0 && !campaign.is_legacy;
  const pendingCount = campaign.pending || 0;
  const showStop = pendingCount > 0 && !campaign.is_legacy;
  const hasMongoTimeout = MONGO_TIMEOUT_FINGERPRINT.test(campaign.sample_error || '');

  return (
    <div data-testid={`campaign-row-${campaign._id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between px-4 py-3 hover:bg-muted/40 text-left transition-colors"
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {isExpanded
            ? <ChevronDown className="w-4 h-4 mt-1 shrink-0 text-muted-foreground" />
            : <ChevronRight className="w-4 h-4 mt-1 shrink-0 text-muted-foreground" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium truncate">{campaign.campaign_name || '(Unnamed)'}</h4>
              {campaign.is_legacy && (
                <Badge variant="outline" className="text-[10px]">Legacy</Badge>
              )}
              {hasMongoTimeout && (
                <Badge variant="destructive" className="text-[10px] gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Mongo timeout
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {campaign.created_by_name && `${campaign.created_by_name} · `}
              {formatDate(campaign.created_at)}
            </p>
            <div className="flex items-center gap-3 mt-1.5 text-xs">
              <span className="text-muted-foreground">Total: <strong>{campaign.total}</strong></span>
              <span className="text-emerald-600">Sent: <strong>{campaign.actual_sent ?? campaign.sent}</strong></span>
              <span className={failedCount > 0 ? 'text-destructive' : 'text-muted-foreground'}>
                Failed: <strong>{failedCount}</strong>
              </span>
              {campaign.pending > 0 && (
                <span className="text-amber-600">Pending: <strong>{campaign.pending}</strong></span>
              )}
            </div>
          </div>
        </div>
        {(showStop || showResend) && (
          <div className="flex items-center gap-2 shrink-0">
            {showStop && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onCancel(); }}
                className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                data-testid={`campaign-stop-${campaign._id}`}
                title={`Stop this campaign — cancels ${pendingCount} pending email${pendingCount === 1 ? '' : 's'}`}
              >
                <StopCircle className="w-3 h-3 mr-1" />
                Stop ({pendingCount})
              </Button>
            )}
            {showResend && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onResend(); }}
                data-testid={`campaign-resend-${campaign._id}`}
              >
                <Send className="w-3 h-3 mr-1" />
                Resend failed ({failedCount})
              </Button>
            )}
          </div>
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pl-11 bg-muted/20" data-testid={`campaign-expanded-${campaign._id}`}>
          {recipientsLoading ? (
            <div className="py-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading recipients…
            </div>
          ) : !recipients || recipients.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">No recipient data.</p>
          ) : (
            <RecipientTable rows={recipients} />
          )}
        </div>
      )}
    </div>
  );
};

const RecipientTable = ({ rows }) => (
  <div className="rounded-md border bg-background overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
        <tr>
          <th className="text-left px-3 py-2 font-medium">Recipient</th>
          <th className="text-left px-3 py-2 font-medium">Org</th>
          <th className="text-left px-3 py-2 font-medium">Status</th>
          <th className="text-left px-3 py-2 font-medium">Sent / Scheduled</th>
          <th className="text-left px-3 py-2 font-medium">Error</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={row.bulk_email_id || row.to_email || i} className="border-t">
            <td className="px-3 py-2">
              <div className="font-medium truncate max-w-[200px]" title={row.contact_email || row.to_email}>
                {row.contact_name || row.to_name || '—'}
              </div>
              <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                {row.contact_email || row.to_email}
              </div>
            </td>
            <td className="px-3 py-2 truncate max-w-[160px]" title={row.org_name}>
              {row.org_name || '—'}
            </td>
            <td className="px-3 py-2">
              <StatusBadge status={row.actual_status || row.status || row.bulk_status} />
            </td>
            <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
              {formatDate(row.sent_at || row.scheduled_at)}
            </td>
            <td className="px-3 py-2 text-xs text-destructive max-w-[280px]">
              {row.error ? (
                <span className="truncate block" title={row.error}>{row.error}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CancelCampaignDialog = ({ open, campaign, cancelling, onClose, onConfirm }) => {
  const pending = campaign?.pending || 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="campaign-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StopCircle className="w-5 h-5 text-destructive" />
            Stop this campaign?
          </DialogTitle>
          <DialogDescription>
            This will cancel <strong>{pending}</strong> pending email{pending === 1 ? '' : 's'}
            {' '}in <em>{campaign?.campaign_name || 'this campaign'}</em>.
            Any email already sent stays sent; any email currently being
            delivered can&apos;t be pulled back.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-3">
          Cancelled rows keep their history so you can see what would have
          been sent. The scheduler will skip them on its next tick.
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={cancelling}
            data-testid="campaign-cancel-abort"
          >
            Keep campaign running
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={cancelling || pending === 0}
            data-testid="campaign-cancel-confirm"
          >
            {cancelling ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Stopping…
              </>
            ) : (
              <>
                <StopCircle className="w-4 h-4 mr-1" />
                Stop campaign
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResendFailedDialog = ({ open, campaign, recipientPreview, onClose, onResent }) => {
  const [onlyMongo, setOnlyMongo] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const failedRows = useMemo(
    () => (recipientPreview || []).filter((r) =>
      (r.actual_status === 'failed' || r.bulk_status === 'failed')
    ),
    [recipientPreview]
  );

  const mongoFailed = useMemo(
    () => failedRows.filter((r) => MONGO_TIMEOUT_FINGERPRINT.test(r.error || '')),
    [failedRows]
  );

  const willResendCount = onlyMongo ? mongoFailed.length : failedRows.length;

  const handleResend = async () => {
    if (!campaign?._id) return;
    setSubmitting(true);
    try {
      const r = await authJsonFetch(`${API}/bulk-email/campaigns/resend-failed`, {
        method: 'POST',
        body: JSON.stringify({
          campaign_id: campaign._id,
          only_mongo_timeouts: onlyMongo,
        }),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.count === 0) {
          toast.warning('No failed emails matched the filter.');
        } else {
          toast.success(
            `Re-queued ${data.count} email${data.count === 1 ? '' : 's'} as "${data.campaign_name}". They'll send within a few minutes.`
          );
          onResent();
        }
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Failed to resend (${r.status})`);
      }
    } catch (err) {
      toast.error('Failed to resend');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="resend-failed-dialog">
        <DialogHeader>
          <DialogTitle>Resend failed emails</DialogTitle>
          <DialogDescription>
            Re-queues every failed recipient in <strong>{campaign?.campaign_name || 'this campaign'}</strong>{' '}
            as a new bulk campaign. The send cron picks them up within a minute or two.
            The original campaign&apos;s records are kept intact for audit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 rounded border bg-muted/40">
            <Checkbox
              id="only-mongo"
              checked={onlyMongo}
              onCheckedChange={(v) => setOnlyMongo(!!v)}
              data-testid="resend-only-mongo-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="only-mongo" className="font-medium cursor-pointer">
                Only retry MongoDB timeouts
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Filters to recipients whose error matches the MongoDB timeout
                fingerprint (e.g. &quot;The read operation timed out&quot;).
                Recommended after an infra blip — leaves genuinely bad email
                addresses (invalid recipient, 550, etc.) alone.
              </p>
            </div>
          </div>

          <div className="rounded border p-3 bg-background">
            <p className="text-sm">
              <strong>{willResendCount}</strong> recipient{willResendCount === 1 ? '' : 's'} will be re-queued.
            </p>
            <div className="text-xs text-muted-foreground mt-1">
              {failedRows.length} total failed · {mongoFailed.length} match MongoDB timeout fingerprint
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleResend}
            disabled={submitting || willResendCount === 0}
            data-testid="resend-failed-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Resend {willResendCount} email{willResendCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignResultsTab;
